// Page Visibility wrapper that emits session_start / session_end events for
// the admin "habit" and "words per active hour" metrics. Behaviour:
//
// - On mount: emit session_start.
// - On `visibilitychange` to hidden: emit session_end with durationMs since
//   the last start.
// - On `visibilitychange` back to visible AFTER ≥30 min hidden: emit a fresh
//   session_start. Resumes within 30 min keep the same session — switching
//   tabs briefly shouldn't fragment the metric.
// - On `pagehide` / `beforeunload`: emit session_end one more time. Browsers
//   throttle async work here, so the log() helper uses keepalive: true.

import { useEffect } from 'react';
import { track } from './observability';
import { Events } from './observability';

const RESUME_THRESHOLD_MS = 30 * 60_000;

export function useSession(): void {
  useEffect(() => {
    let sessionStartTs = Date.now();
    let lastHiddenTs: number | null = null;

    track(Events.SessionStart);

    function handleVisibility() {
      if (document.visibilityState === 'hidden') {
        const now = Date.now();
        const dur = now - sessionStartTs;
        track(Events.SessionEnd, { durationMs: dur });
        lastHiddenTs = now;
      } else {
        if (lastHiddenTs !== null && Date.now() - lastHiddenTs >= RESUME_THRESHOLD_MS) {
          sessionStartTs = Date.now();
          track(Events.SessionStart);
        }
        lastHiddenTs = null;
      }
    }

    function handlePageHide() {
      track(Events.SessionEnd, { durationMs: Date.now() - sessionStartTs });
    }

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pagehide', handlePageHide);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);
}
